import React, { useEffect, useMemo, useState } from 'react'
import {
  DataGrid,
  GridPreferencePanelsValue,
  GridToolbarContainer,
  useGridApiContext,
} from '@mui/x-data-grid'
import { Box, Chip, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import Button from '@mui/material/Button'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import success from 'Components/images/success.svg'
import failed from 'Components/images/failure.svg'
import moment from 'moment'
import { upperCase } from 'lodash'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'

// LAP — Redesign tableau Mes Comptes.
// - en-têtes contrastées + uppercase
// - hover/zebra
// - solde formatté (Intl.NumberFormat) + couleur vert/rouge
// - IBAN/numéro masqué en monospace
// - chip pour le type de compte
// - empty state propre
// - éclairé en pagination serveur (DRF) inchangé
function formatMoney(value, currency = 'EUR', locale = 'fr-FR') {
  const num = Number(value)
  if (!Number.isFinite(num)) return '—'
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: (currency || 'EUR').toUpperCase(),
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  } catch (e) {
    return `${num.toFixed(2)} ${currency || ''}`.trim()
  }
}

function maskIban(iban) {
  if (!iban) return ''
  const s = String(iban).replace(/\s+/g, '')
  if (s.length <= 8) return s
  const head = s.slice(0, 4)
  const tail = s.slice(-4)
  const masked = '•'.repeat(Math.max(0, s.length - 8))
  // Re-groupe par 4 pour la lisibilité
  return `${head} ${masked.replace(/(.{4})/g, '$1 ').trim()} ${tail}`
}

function maskAccountNumber(numero) {
  if (!numero) return ''
  const s = String(numero)
  if (s.length <= 4) return s
  return `••• ${s.slice(-4)}`
}

function ListOperations({ fetchOperations, operations, setSelectedRows, setOperations }) {
  const { t, i18n } = useTranslation('common')
  const [modifiedColumns, setModifiedColumns] = useState([])
  const [rows, setRows] = useState(20)
  const [page, setPage] = useState(0)
  let navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const locale = useMemo(() => {
    switch ((i18n.language || 'fr').slice(0, 2)) {
      case 'en':
        return 'en-US'
      case 'de':
        return 'de-DE'
      case 'it':
        return 'it-IT'
      case 'es':
        return 'es-ES'
      default:
        return 'fr-FR'
    }
  }, [i18n.language])

  const desktopColumns = useMemo(
    () => [
      {
        field: 'Nom',
        headerName: t('mesComptes.columns.name'),
        flex: 1.4,
        minWidth: 180,
        headerClassName: 'mc-header',
        renderCell: (params) => (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: '#e8eef9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0d1b3d',
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {(params.row.Nom?.Nom || params.row.account_name || '?').slice(0, 2).toUpperCase()}
            </Box>
            <Box sx={{ overflow: 'hidden' }}>
              <Box
                sx={{
                  fontWeight: 600,
                  color: '#0d1b3d',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                }}
              >
                {params.row.Nom?.Nom || params.row.account_name || '—'}
              </Box>
              <Box sx={{ fontSize: 12, color: '#6b7790' }}>
                {params.row.TypeCompte?.TypeCompte || params.row.account_type || '—'}
              </Box>
            </Box>
          </Stack>
        ),
      },
      {
        field: 'TypeCompte',
        headerName: t('mesComptes.columns.accountType'),
        width: 160,
        headerClassName: 'mc-header',
        valueGetter: (params) => params.row.TypeCompte?.TypeCompte || params.row.account_type || '',
        renderCell: (params) => {
          const value = params.value
          if (!value) return <span style={{ color: '#9aa3b8' }}>—</span>
          return (
            <Chip
              label={value}
              size="small"
              sx={{
                bgcolor: '#eef3ff',
                color: '#0d3a7a',
                fontWeight: 500,
                borderRadius: 1.5,
              }}
            />
          )
        },
      },
      {
        field: 'IBAN',
        headerName: t('mesComptes.columns.iban'),
        flex: 1.1,
        minWidth: 200,
        headerClassName: 'mc-header',
        renderCell: (params) => (
          <Tooltip title={params.row.IBAN || ''}>
            <Box
              component="span"
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 12,
                letterSpacing: 0.3,
                color: '#3a4661',
              }}
            >
              {maskIban(params.row.IBAN)}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'NumeroCompte',
        headerName: t('mesComptes.columns.accountNumber'),
        width: 140,
        headerClassName: 'mc-header',
        renderCell: (params) => (
          <Tooltip title={params.row.NumeroCompte || ''}>
            <Box
              component="span"
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 12,
                color: '#3a4661',
              }}
            >
              {maskAccountNumber(params.row.NumeroCompte)}
            </Box>
          </Tooltip>
        ),
      },
      {
        field: 'balance',
        headerName: t('mesComptes.columns.balance'),
        width: 160,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        headerClassName: 'mc-header',
        renderCell: (params) => {
          const num = Number(params.row.balance)
          const color = !Number.isFinite(num) || num === 0 ? '#3a4661' : num > 0 ? '#1f7a3d' : '#c0392b'
          return (
            <Box
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontWeight: 600,
                color,
                fontSize: 14,
              }}
            >
              {formatMoney(params.row.balance, params.row.Devise, locale)}
            </Box>
          )
        },
      },
      {
        field: 'NumeroClient',
        headerName: t('mesComptes.columns.clientNumber'),
        width: 130,
        headerClassName: 'mc-header',
        renderCell: (params) => (
          <Box sx={{ fontSize: 13, color: '#3a4661' }}>{params.row.NumeroClient || '—'}</Box>
        ),
      },
      {
        field: 'sync_status',
        headerName: t('mesComptes.columns.syncStatus'),
        width: 110,
        align: 'center',
        headerAlign: 'center',
        headerClassName: 'mc-header',
        renderCell: (params) => {
          if (params.row.sync_status === 'Success')
            return (
              <Tooltip title={t('mesComptes.columns.syncStatus') + ' OK'}>
                <img style={{ height: 28 }} src={success} alt="" />
              </Tooltip>
            )
          if (params.row.sync_status === 'Failed')
            return (
              <Tooltip title={t('mesComptes.columns.syncStatus') + ' KO'}>
                <img style={{ height: 28 }} src={failed} alt="" />
              </Tooltip>
            )
          return <span style={{ color: '#9aa3b8' }}>—</span>
        },
      },
      {
        field: 'last_sync_date',
        headerName: t('mesComptes.columns.lastSyncDate'),
        width: 150,
        headerClassName: 'mc-header',
        renderCell: (params) => {
          if (!params.row.last_sync_date) return <span style={{ color: '#9aa3b8' }}>—</span>
          return (
            <Box sx={{ fontSize: 12, color: '#6b7790' }}>
              {moment(params.row.last_sync_date, 'YYYY-MM-DDThh:mm:ss').locale(i18n.language || 'fr').fromNow()}
            </Box>
          )
        },
      },
    ],
    [t, i18n.language, locale],
  )

  const mobileColumns = useMemo(
    () => [
      {
        field: 'Nom',
        headerName: t('mesComptes.columns.name'),
        flex: 1,
        headerClassName: 'mc-header',
        renderCell: (params) => (
          <Box sx={{ py: 1.5 }}>
            <Box sx={{ fontWeight: 600, color: '#0d1b3d', fontSize: 14 }}>
              {params.row.Nom?.Nom || params.row.account_name || '—'}
            </Box>
            <Box sx={{ fontSize: 12, color: '#6b7790', mt: 0.5 }}>
              {params.row.TypeCompte?.TypeCompte || params.row.account_type || '—'}
            </Box>
            <Box
              sx={{
                fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                fontSize: 11,
                color: '#3a4661',
                mt: 0.5,
              }}
            >
              {maskIban(params.row.IBAN)}
            </Box>
          </Box>
        ),
      },
      {
        field: 'balance',
        headerName: t('mesComptes.columns.balance'),
        flex: 1,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        cellClassName: 'dtcell',
        headerClassName: 'mc-header',
        renderCell: (params) => {
          const num = Number(params.row.balance)
          const color = !Number.isFinite(num) || num === 0 ? '#3a4661' : num > 0 ? '#1f7a3d' : '#c0392b'
          return (
            <Box sx={{ pt: 1.5 }}>
              <Box
                sx={{
                  fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
                  fontWeight: 700,
                  color,
                  fontSize: 14,
                }}
              >
                {formatMoney(params.row.balance, params.row.Devise, locale)}
              </Box>
              <Box sx={{ fontSize: 10, color: '#9aa3b8', mt: 0.5 }}>
                {upperCase(params.row.Devise)}
              </Box>
            </Box>
          )
        },
      },
    ],
    [t, locale],
  )

  useEffect(() => {
    setModifiedColumns(isMobile ? mobileColumns : desktopColumns)
  }, [isMobile, desktopColumns, mobileColumns])

  const handleRowsChange = (newPageSize) => {
    setRows(newPageSize)
    let url = `&limit=${newPageSize}&offset=${page}`
    fetchOperations(url)
  }
  const handlePageChange = (newPage) => {
    setPage(newPage * rows)
    let url = `&limit=${rows}&offset=${newPage * rows}`
    fetchOperations(url)
  }

  const CustomToolbar = () => {
    const apiRef = useGridApiContext()
    const handleClick = () => {
      apiRef.current?.showPreferences(GridPreferencePanelsValue.columns)
    }
    return (
      <GridToolbarContainer>
        <Button id="cus-show-column" onClick={handleClick} sx={{ display: 'none' }} />
      </GridToolbarContainer>
    )
  }

  const NoRowsOverlay = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: '#6b7790', gap: 1 }}>
      <AccountBalanceWalletIcon sx={{ fontSize: 48, color: '#c4cbdb' }} />
      <Typography variant="body2" sx={{ fontWeight: 500 }}>
        {t('mesComptes.titleWithCount', { count: 0 })}
      </Typography>
      <Typography variant="caption" sx={{ color: '#9aa3b8' }}>
        {t('mesComptes.search.placeholder')}
      </Typography>
    </Stack>
  )

  return (
    <div
      style={{
        width: '100%',
        background: 'white',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(13,27,61,0.08)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #eef0f4',
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: '#0d1b3d', fontSize: 16 }}
        >
          {t('mesComptes.titleWithCount', { count: operations.count || 0 })}
        </Typography>
      </Box>
      <DataGrid
        sx={{
          border: 0,
          height: 640,
          fontSize: isMobile ? '12px' : '13px',
          '.dtcell': { alignItems: 'flex-start' },
          '& .mc-header': {
            backgroundColor: '#f4f6fb',
            color: '#0d1b3d',
            fontWeight: 700,
            textTransform: 'uppercase',
            fontSize: 11,
            letterSpacing: 0.5,
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid #e3e8f1',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #f1f3f8',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#f7f9fc',
            cursor: 'pointer',
          },
          '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: '#e8eef9',
            '&:hover': { backgroundColor: '#dde6f5' },
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid #eef0f4',
          },
        }}
        rows={operations.results ? operations.results : []}
        columns={modifiedColumns}
        pageSize={rows}
        getRowHeight={() => (isMobile ? 'auto' : 64)}
        rowCount={operations.count ? operations.count : 0}
        onPageSizeChange={handleRowsChange}
        onPageChange={handlePageChange}
        rowsPerPageOptions={[10, 20, 40]}
        pagination
        onSelectionModelChange={(itm) => setSelectedRows([...itm])}
        paginationMode="server"
        checkboxSelection
        disableSelectionOnClick
        onRowClick={(params) => {
          navigate(`/compte/${params.row.id}`)
        }}
        components={{
          Toolbar: CustomToolbar,
          NoRowsOverlay,
        }}
      />
    </div>
  )
}
export default ListOperations
